import { Checkbox, DatePicker, Form, FormInstance, Input, Radio, Select } from "antd";

interface DynamicFieldProps {
  field: any;
  form: FormInstance;
  styles: any;
  setSearchValue: (val: string) => void;
}

const DynamicField: React.FC<DynamicFieldProps> = ({ field, form, styles, setSearchValue }) => {
  const shouldRenderOther = field.type === "radioWithOther" || field.type === "checkboxWithOther";

  const renderInputByType = () => {
    switch (field.type) {
      case "text":
        return <Input placeholder={field.placeholder} />;

      case "checkbox":
        return <Checkbox />;

      case "checkboxWithOther":
        return (
          <Checkbox.Group
            options={field.options.map((opt: string) => ({
              label: opt,
              value: opt,
            }))}
          />
        );

      case "datepicker":
        return <DatePicker format="DD-MM-YYYY" className="h-[45px] w-full" />;

      case "select":
        return (
          <Select
            placeholder="Select"
            className={styles.customSelect}
            showSearch
            optionFilterProp="label"
            filterOption={false}
            onSearch={(value) => {
              if (field.id === "occupation") {
                setSearchValue(value);
              }
            }}
            disabled={field.disabled}
          >
            {field.options.map((opt: string | { id: number; value: string }, index: number) =>
              typeof opt === "string" ? (
                <Select.Option key={`${opt}-${index}`} value={opt}>
                  {opt}
                </Select.Option>
              ) : (
                <Select.Option key={opt.id ?? `${opt.value}-${index}`} value={opt.id}>
                  {opt.value}
                </Select.Option>
              ),
            )}
          </Select>
        );

      case "radio":
      case "radioWithOther":
        return (
          <Radio.Group className={styles.radioGroup} disabled={field.disabled}>
            {field.options.map((opt: string) => (
              <Radio key={opt} value={opt}>
                {opt}
              </Radio>
            ))}
          </Radio.Group>
        );

      case "textarea":
        return (
          <Input.TextArea
            placeholder={field.placeholder}
            rows={2}
            className={styles.textAreaNoResize}
          />
        );

      default:
        return null;
    }
  };

  if (field.type === "span") {
    return <div className="text-gray-700 mt-1 mb-5">{field.value}</div>;
  }

  return (
    <>
      <Form.Item
        name={field.id}
        className={styles.customForm}
        initialValue={field.type === "checkbox" ? false : undefined}
        valuePropName={field.type === "checkbox" ? "checked" : "value"}
        rules={
          field.type === "checkbox"
            ? []
            : [
                {
                  required: field?.required !== false,
                  message: `${field.label} is required`,
                },
              ]
        }
      >
        {renderInputByType()}
      </Form.Item>

      {shouldRenderOther && (
        <Form.Item
          name={`${field.id}_other`}
          dependencies={[field.id]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                const selected = getFieldValue(field.id);

                if (
                  (Array.isArray(selected) && selected.includes("Others") && !value) ||
                  (selected === "Others" && !value)
                ) {
                  return Promise.reject(new Error("Please specify other value"));
                }

                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input />
        </Form.Item>
      )}
    </>
  );
};

export default DynamicField;
